import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  auth,
  signInWithEmail,
  signInWithGoogle,
  signInWithMicrosoft,
  handleRedirectResult,
  signOut as firebaseSignOut,
  onAuthChange,
  getIdToken,
  FUNCTIONS_URL
} from '../firebase';

// 建立 Context
const AuthContext = createContext(null);

// Provider 元件
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 獲取使用者資料
  const fetchUserProfile = useCallback(async (firebaseUser) => {
    if (!firebaseUser) {
      setUserProfile(null);
      return null;
    }

    try {
      const token = await firebaseUser.getIdToken();

      // 記錄登入並獲取使用者資料
      const response = await fetch(`${FUNCTIONS_URL}/api/users/login-record`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('獲取使用者資料失敗');
      }

      const result = await response.json();

      if (result.success) {
        setUserProfile(result.data);
        return result.data;
      } else {
        throw new Error(result.error?.message || '獲取使用者資料失敗');
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
      setError(err.message);
      return null;
    }
  }, []);

  // Handle redirect result on page load
  useEffect(() => {
    handleRedirectResult()
      .then(async (result) => {
        if (result?.user) {
          await fetchUserProfile(result.user);
        }
      })
      .catch((err) => {
        console.error('Redirect sign-in error:', err);
        setError(getAuthErrorMessage(err.code));
        setLoading(false);
      });
  }, [fetchUserProfile]);

  // 監聽認證狀態變化
  useEffect(() => {
    const unsubscribe = onAuthChange(async (firebaseUser) => {
      setUser(firebaseUser);

      if (firebaseUser) {
        await fetchUserProfile(firebaseUser);
      } else {
        setUserProfile(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [fetchUserProfile]);

  // 使用 Email/Password 登入
  const login = async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      const result = await signInWithEmail(email, password);
      await fetchUserProfile(result.user);
      return { success: true, user: result.user };
    } catch (err) {
      const errorMessage = getAuthErrorMessage(err.code);
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // 使用 Google 登入 (Safari 用 popup，其他用 redirect)
  const loginWithGoogle = async () => {
    try {
      setError(null);
      setLoading(true);
      const result = await signInWithGoogle();
      // popup 模式直接回傳 result，redirect 模式由 handleRedirectResult 處理
      if (result?.user) {
        await fetchUserProfile(result.user);
        return { success: true, user: result.user };
      }
    } catch (err) {
      const errorMessage = getAuthErrorMessage(err.code);
      setError(errorMessage);
      setLoading(false);
      return { success: false, error: errorMessage };
    }
  };

  // 使用 Microsoft 登入
  const loginWithMicrosoft = async () => {
    try {
      setError(null);
      setLoading(true);
      const result = await signInWithMicrosoft();
      if (result?.user) {
        await fetchUserProfile(result.user);
        return { success: true, user: result.user };
      }
    } catch (err) {
      const errorMessage = getAuthErrorMessage(err.code);
      setError(errorMessage);
      setLoading(false);
      return { success: false, error: errorMessage };
    }
  };

  // 登出
  const logout = async () => {
    try {
      setError(null);
      await firebaseSignOut();
      setUser(null);
      setUserProfile(null);
      return { success: true };
    } catch (err) {
      const errorMessage = err.message || '登出失敗';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // 重新整理使用者資料
  const refreshUserProfile = async () => {
    if (user) {
      return await fetchUserProfile(user);
    }
    return null;
  };

  // 獲取 Authorization Header
  const getAuthHeader = async () => {
    const token = await getIdToken();
    if (!token) {
      return {};
    }
    return {
      'Authorization': `Bearer ${token}`,
    };
  };

  // 檢查是否有指定權限
  const hasPermission = (permission) => {
    if (!userProfile?.permissions) {
      return false;
    }
    return userProfile.permissions.includes(permission);
  };

  // Context 值
  const value = {
    // 狀態
    user,
    userProfile,
    loading,
    error,
    isAuthenticated: !!user,

    // 認證方法
    login,
    loginWithGoogle,
    loginWithMicrosoft,
    logout,
    refreshUserProfile,
    getAuthHeader,
    getIdToken,

    // 權限檢查
    hasPermission,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// 自訂 Hook
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// 錯誤訊息轉換
function getAuthErrorMessage(errorCode) {
  const errorMessages = {
    'auth/invalid-email': '無效的電子郵件格式',
    'auth/user-disabled': '此帳號已被停用',
    'auth/user-not-found': '找不到此使用者',
    'auth/wrong-password': '密碼錯誤',
    'auth/invalid-credential': '帳號或密碼錯誤',
    'auth/email-already-in-use': '此電子郵件已被使用',
    'auth/weak-password': '密碼強度不足，請使用至少 6 個字元',
    'auth/operation-not-allowed': '此登入方式尚未啟用',
    'auth/popup-closed-by-user': '登入視窗已關閉',
    'auth/cancelled-popup-request': '登入已取消',
    'auth/popup-blocked': '登入視窗被瀏覽器封鎖，請允許彈出視窗',
    'auth/network-request-failed': '網路連線失敗，請檢查網路狀態',
    'auth/too-many-requests': '登入嘗試次數過多，請稍後再試',
    'auth/requires-recent-login': '請重新登入後再執行此操作',
  };

  return errorMessages[errorCode] || '登入失敗，請稍後再試';
}

export default AuthContext;
